// Packages
import React from 'react';
import { createPortal } from 'react-dom';

// Components
import { KiteDialog, KiteFormDialog } from '@kite/react-kite';

// Utils

// Styles
import './ModalWrapper.scss';

/** Wrapper for KiteDialogs that opens it in a Portal component */

export interface IModalWrapperProps {
  /**  Children to render in the modal */
  children: any;
  /** Boolean for is modal is open */
  canShow: boolean;
  /** Closed modal */
  onHide: () => void;
  /** Primary Button Label */
  ctaCopy: string;
  /** Primary Button Action */
  ctaAction: () => void;
  /** Show loader for Primary Button Action */
  ctaLoading?: boolean;

  /** Optional KiteDialog Porperties */
  title?: string;
  /**
   * Identifier for the dialog, appended to Kite's `rk_dialog_` prefix and used to
   * build its `aria-labelledby`.
   *
   * Defaults to the FIRST class in `className`. It used to be the whole
   * `className`, which broke every dialog whose class list had more than one
   * entry: `"delete-user-modal se-icon--status"` became an id containing a space,
   * so `aria-labelledby` resolved to two ids that do not exist and the dialog was
   * announced with no name at all (ENTDEL-31827). Pass this explicitly when the
   * first class is not a good identifier.
   */
  dialogId?: string;
  secondaryCtaCopy?: string;
  secondaryCtaAction?: () => void | null;
  className?: string;
  /** String to append to component's "id" attribute, after "rk_dialog_" */
  icon?: string;
  /** Color of the icon */
  iconColor?: string;
  /** Whether this dialog contains form inputs */
  isFormDialog?: boolean;
  /** Optional property to swap button order */
  reverseButtonOrder?: boolean;
  /** Display modal as full page if true */
  fullPage?: boolean;
}

const ModalWrapper = ({
  children,
  canShow,
  onHide,
  title,
  ctaAction,
  ctaCopy,
  ctaLoading,
  secondaryCtaAction,
  secondaryCtaCopy,
  className,
  dialogId,
  icon,
  iconColor,
  isFormDialog = false,
  reverseButtonOrder,
  fullPage,
}: IModalWrapperProps) => {
  let portalRoot = document.getElementById('portal-root');

  if (!portalRoot) {
    const rootContainer = document.createElement('div');
    rootContainer.setAttribute('id', 'portal-root');
    portalRoot = rootContainer;
  }

  const iconStyle = iconColor
    ? ({ '--modal-icon-color': iconColor } as React.CSSProperties)
    : undefined;

  /**
   * A valid HTML id: the first class only, since an id cannot contain
   * whitespace. Kite derives the dialog's `aria-labelledby` from this, so a
   * multi-class value produced a name that referenced nothing.
   */
  const resolvedDialogId =
    dialogId || className?.trim().split(/\s+/)[0] || undefined;

  const dialog = isFormDialog ? (
    <KiteFormDialog
      className={`modal-wrapper${fullPage ? ' modal-wrapper__full-page' : ''}${
        className ? ` ${className}` : ''
      }${iconColor ? ' modal-wrapper--custom-icon-color' : ''}`}
      open={canShow}
      title={title || ''}
      onClose={onHide}
      allowClickAway
      submitBtnLabel={ctaCopy}
      onSubmit={ctaAction}
      secondaryBtnLabel={secondaryCtaCopy}
      onSecondaryBtnClick={secondaryCtaAction}
      icon={icon}
      style={iconStyle}
    >
      {children}
    </KiteFormDialog>
  ) : (
    <KiteDialog
      className={`modal-wrapper${fullPage ? ' modal-wrapper__full-page' : ''}${
        className ? ` ${className}` : ''
      }${iconColor ? ' modal-wrapper--custom-icon-color' : ''}`}
      open={canShow}
      title={title || ''}
      onClose={onHide}
      id={resolvedDialogId}
      /**
       * Declares the dialog as blocking, so assistive technology treats it as a
       * decision to answer rather than as more page content. Kite forwards
       * unknown props onto the element.
       */
      aria-modal="true"
      allowClickAway
      primaryBtnLabel={ctaCopy}
      onPrimaryBtnClick={ctaAction}
      primaryBtnLoading={ctaLoading}
      secondaryBtnLabel={secondaryCtaCopy}
      onSecondaryBtnClick={secondaryCtaAction}
      icon={icon}
      reverseButtonOrder={reverseButtonOrder}
      style={iconStyle}
    >
      {children}
    </KiteDialog>
  );
  return createPortal(dialog, portalRoot) as React.ReactNode;
};

export default ModalWrapper;
